import { IAttributes, IOrder } from '@checkout/payments';
import { IAddress, IIngridAddress, IIngridShippingInfo, RootState } from '@checkout/state';
import * as cx from 'classnames';
import * as React from 'react';
import { Fragment } from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { nosto } from 'ys-analytics';
import { contextCheckoutConfig } from '../../../config/contextCheckoutConfig';
import { paymentResultRequest } from '../../../reducers/checkout';
import { arrowNextIcon, orderSuccessIcon } from '../../../utils/icons';
import messages from './../../../i18n/base-en';
import logos from './../../../img/carriers';
import { isJsonString, slugify } from './../../../utils/utils';
import { LoaderSpinner, Summary, Widget } from './../../presentational';
import * as Styles from './Receipt.scss';

interface IOwnProps { }

interface IConnectedProps {
  order: IOrder;
  isFetching: boolean;
}

interface IConnectetDispatchProps {
  paymentResultRequest: typeof paymentResultRequest;
}

type Props = IOwnProps & IConnectedProps & IConnectetDispatchProps & InjectedIntlProps;

class Receipt extends React.Component<Props, {}> {

  handleNosto = (order: IOrder | undefined): void => {
    const { nostoPurchase } = nosto;
    if (order && order.items && order.items && order.items.length > 0) {
      try {
        nostoPurchase(order);
      } catch (err) {
        // tslint:disable-next-line:no-console
        console.error('Enhanced Ecommerce error: ', err);
      }
    }
  }

  componentDidMount() {
    this.props.paymentResultRequest();
  }

  componentDidUpdate() {
    const { order } = this.props;
    this.handleNosto(order);
    document.addEventListener('ysAnalytics/loadNostojs', () => {
      this.handleNosto(order);
    });
  }

  getLogoForCarrier = (shippingMethod: string) => {
    const { dhlServicePoint, dhlExpress, dhlPaket, babyshop, caliroots, kronans, ups, apotekHjartat, lekmerSe, uropenn } = logos;
    // tslint:disable-next-line:switch-default
    switch (shippingMethod) {
      case 'dhl-fsp':
      case 'dhl-svp':
        return dhlServicePoint;
      case 'dhl-esl':
      case 'dhl-eww':
        return dhlExpress;
      case 'dhl-par':
        return dhlPaket;
      case 'ics-bbs':
        return babyshop;
      case 'ics-clrg':
      case 'ics-clrs':
        return caliroots;
      case 'ics-kra':
        return kronans;
      case 'ups-std':
        return ups;
      case 'ics-ahj':
      case 'ics-ahjc':
      case 'ics-ahje':
        return apotekHjartat;
      case 'ics-lkm':
        return lekmerSe;
      case 'ics-urp':
        return uropenn;
    }

    const carrier = shippingMethod.split('-')[0];

    let carrierLogo = null;

    if (carrier) {
      if (logos[carrier]) {
        return logos[carrier];
      }

      try {
        carrierLogo = require(`./../../../img/carriers/${carrier}.png`);
      } catch (error) {
        // tslint:disable-next-line:no-console
        console.log(error);
      }
    }

    return carrierLogo;
  }

  renderAddressSection = (title: string, orderDetailsClassNames: string, addressData: IAddress) => {
    const { intl } = this.props;
    const { formatMessage } = intl;

    return (
      <Fragment>
        <div className={Styles.title}>{title}</div>

        <div className={orderDetailsClassNames}>
          <span>{formatMessage(messages.order_address_name)}</span>
          <span className={Styles.value}>{addressData.firstName} {addressData.lastName}</span>
        </div>

        {addressData.company && (
          <div className={orderDetailsClassNames}>
            <span>{formatMessage(messages.order_address_company)}</span>
            <span className={Styles.value}>{addressData.company}</span>
          </div>
        )}

        <div className={orderDetailsClassNames}>
          <span>{formatMessage(messages.order_address_address)}</span>
          <span className={Styles.value}>{addressData.address1}{addressData.address2 && `, ${addressData.address2}`}</span>
        </div>

        <div className={orderDetailsClassNames}>
          <span>{formatMessage(messages.order_address_city)}</span>
          <span className={Styles.value}>{addressData.city}</span>
        </div>

        <div className={orderDetailsClassNames}>
          <span>{formatMessage(messages.order_address_zipcode)}</span>
          <span className={Styles.value}>{addressData.zipCode}</span>
        </div>

        <div className={orderDetailsClassNames}>
          <span>{formatMessage(messages.order_address_country)}</span>
          {addressData.state ?
            <span className={Styles.value}>{addressData.state}, {addressData.countryName}</span> :
            <span className={Styles.value}>{addressData.countryName}</span>
          }
        </div>
      </Fragment>
    );
  }

  getIngridAddressData = (shippingInfo?: IIngridShippingInfo): IIngridAddress | undefined => {
    if (!shippingInfo) { return undefined; }

    const { session } = shippingInfo;
    const { selected_shipping_option, customer } = session;
    const { location } = selected_shipping_option;
    const { search_address } = shippingInfo.session;
    let address: IIngridAddress | undefined = search_address;

    switch (selected_shipping_option.delivery_type) {
      case 'pickup':
      case 'instore':
        if (location) {
          address = location.address;
        } else {
          address = customer.address;
        }
        break;
      case 'delivery':
      case 'mailbox':
        if (search_address) {
          address = search_address;
        } else {
          address = customer.address;
        }
        break;
      default:
        if (search_address) {
          address = search_address;
        } else {
          address = customer.address;
        }
    }

    return address;
  }

  renderIngridShippingInfo = (title: string, orderDetailsClassNames: string, attributes?: IAttributes) => {
    if (!attributes) { return null; }

    const deliveryTime = attributes && attributes.shipwallet_deliverytime.data;
    const doorcode = attributes && attributes.shipwallet_doorcode.data;
    const shippingInfo = attributes && JSON.parse(attributes.ingrid_raw_session_response.data) as IIngridShippingInfo;
    const ingridShippingAddressData = this.getIngridAddressData(shippingInfo);
    const { session } = shippingInfo;
    const { selected_shipping_option } = session;
    const { intl } = this.props;
    const { formatMessage } = intl;
    const carrierLogo = this.getLogoForCarrier(selected_shipping_option.shipping_method);

    return (
      <Fragment>
        <div className={Styles.title}>{title}</div>
        <div className={Styles.row}>
          {selected_shipping_option && selected_shipping_option.carrier && shippingInfo.session.selected_shipping_option.delivery_type !== 'instore' && (
            <div className={orderDetailsClassNames}>
              <span>{formatMessage(messages.ingrid_carrier_label)}</span>
              <div className={Styles.value}>
                {!carrierLogo && <span className={selected_shipping_option.shipping_method}>{selected_shipping_option.carrier}</span>}
                {carrierLogo && (
                  <div className={Styles.carrierLogoContainer}>
                    <img className={Styles.carrierLogo} alt={selected_shipping_option.carrier} src={carrierLogo} />
                  </div>
                )}
              </div>
            </div>
          )}
          <div className={orderDetailsClassNames}>
            <span>{formatMessage(messages.ingrid_name_label)}</span>
            <span className={Styles.value}>{ingridShippingAddressData && ingridShippingAddressData.name}</span>
          </div>
          <div className={orderDetailsClassNames}>
            <span>{formatMessage(messages.ingrid_address_label)}</span>
            <div className={Styles.addressWrapper}>
              {ingridShippingAddressData &&
                ingridShippingAddressData.address_lines &&
                ingridShippingAddressData.address_lines.map((line: string) => (
                <span key={line} className={Styles.value}>{line}</span>
              ))}
            </div>
          </div>
          <div className={orderDetailsClassNames}>
            <span>{formatMessage(messages.ingrid_city_label)}</span>
            <span className={Styles.value}>{ingridShippingAddressData && ingridShippingAddressData.city}</span>
          </div>
          <div className={orderDetailsClassNames}>
            <span>{formatMessage(messages.ingrid_zipcode_label)}</span>
            <span className={Styles.value}>{ingridShippingAddressData && ingridShippingAddressData.postal_code}</span>
          </div>
          {doorcode && <div className={orderDetailsClassNames}>
            <span>{formatMessage(messages.ingrid_doorcode)}</span>
            <span className={Styles.value}>{doorcode}</span>
          </div>}
          {deliveryTime && (
            <div className={orderDetailsClassNames}>
              <span>{formatMessage(messages.ingrid_estimated_delivery_time_label)}</span>
              <span className={Styles.value}>{deliveryTime}</span>
            </div>
          )}
        </div>
      </Fragment>
    );
  }

  render() {
    const { order, isFetching, intl } = this.props;
    const { formatMessage } = intl;
    const {
      attributes,
      date,
      order: orderId,
      statusDescription,
      totals,
      items,
      address,
      shippingAddress,
      paymentMethodName,
      shippingMethodName,
      paymentMethodData,
      affiliateHtml } = order;

    const isIngridInfoAvailable = attributes &&
      attributes.ingrid_raw_session_response &&
      isJsonString(attributes.ingrid_raw_session_response.data);

    // if (Object.keys(order).length === 0 && order.constructor === Object) { return null; }

    const orderDetailsClassNames = cx({
      [Styles.row]: true,
      [Styles.orderDetailsRow]: true,
    });

    const bottomSectionClassNames = cx({
      [Styles.section]: true,
      [Styles.bottomSection]: true,
    });

    const billingAddressColumnClassNames = cx({
      [Styles.column]: !isIngridInfoAvailable,
      [Styles.columnFullWidth]: isIngridInfoAvailable !== undefined,
    });

    return (
      <div className={Styles.receipt}>
        {isFetching && (
          <LoaderSpinner width={70} height={70} />
        )}

        {!isFetching && Object.keys(order).length > 0 && (
          <Fragment>
            {paymentMethodData && paymentMethodData.snippet && <Widget snippet={paymentMethodData.snippet} />}

            <div className={Styles.head}>
              <div className={Styles.icon}>{orderSuccessIcon}</div>
              <p className={Styles.header}>{formatMessage(messages.thank_message)}</p>
            </div>
            <div className={Styles.title}>{formatMessage(messages.order_details_label)}</div>
            <div className={Styles.columns}>
              <div className={Styles.column}>
                <div className={orderDetailsClassNames}>
                  <span>{formatMessage(messages.order_number_label)}</span>
                  <span className={Styles.value}>{orderId}</span>
                </div>
                <div className={orderDetailsClassNames}>
                  <span>{formatMessage(messages.order_date_label)}</span>
                  <span className={Styles.value}>{date}</span></div>
                <div className={orderDetailsClassNames}>
                  <span>{formatMessage(messages.order_status_label)}</span>
                  <span className={Styles.value}>{statusDescription}</span>
                </div>
              </div>
              <div className={Styles.column}>
                {paymentMethodName && (
                    <div className={orderDetailsClassNames} data-block-for="ReceiptPaymentMethod" data-payment-method={slugify(paymentMethodName)}>
                      <span>{formatMessage(messages.payment_method_receipt_title)}</span>
                      <span className={Styles.value}>{paymentMethodName}</span>
                    </div>
                  )
                }
                {shippingMethodName && (
                    <div className={orderDetailsClassNames} data-block-for="ReceiptShippingMethod" data-shipping-method={slugify(shippingMethodName)}>
                      <span>{formatMessage(messages.delivery_method_receipt_title)}</span>
                      <span className={Styles.value}>{shippingMethodName}</span>
                    </div>
                  )
                }
              </div>
            </div>
            <div className={Styles.columns}>
              {!isIngridInfoAvailable && (
                <div className={Styles.column} data-block-for="ReceiptShippingAddress">
                  {this.renderAddressSection(formatMessage(messages.shipping_address_receipt_title), orderDetailsClassNames, shippingAddress)}
                </div>
              )}
              <div className={billingAddressColumnClassNames} data-block-for="ReceiptBillingAddress">
                {this.renderAddressSection(formatMessage(messages.billing_address_receipt_title), orderDetailsClassNames, address)}
              </div>
            </div>

            {attributes && isIngridInfoAvailable && (
              <div className={Styles.columns}>
                <div className={Styles.columnFullWidth} data-block-for="ReceiptIngridShippingAddress">
                  {this.renderIngridShippingInfo(formatMessage(messages.ingrid_info_receipt_title), orderDetailsClassNames, attributes)}
                </div>
              </div>
            )}

            <div className={Styles.section}>
              <Summary totals={totals} items={items} title={formatMessage(messages.order_summary_receipt_title)} />
            </div>
            <div className={bottomSectionClassNames}>
              <button className={Styles.button} onClick={window.print}>{formatMessage(messages.print_receipt_label)}</button>
              <a href="/" className={Styles.button}>{formatMessage(messages.continue_shopping_receipt_link_label)}{arrowNextIcon}</a>
            </div>

            {(contextCheckoutConfig.modules.showPrisJakt.isEnabled || contextCheckoutConfig.modules.showPriceRunner.isEnabled)
            && <div className={Styles.section}>
              <div className={Styles.externalLinks}>
                <a href={contextCheckoutConfig.modules.showPrisJakt.url} target="_blank" className={Styles.prisJakt}/>
                <a href={contextCheckoutConfig.modules.showPriceRunner.url} target="_blank" className={Styles.priceRunner}/>
              </div>
            </div>
            }

            {affiliateHtml && <Widget snippet={ affiliateHtml} />}
          </Fragment>
        ) }
      </div>
    );
  }
}

export default compose<React.ComponentClass>(
  injectIntl,
  connect<IConnectedProps, IConnectetDispatchProps>(
    (state: RootState) => ({
      order: state.checkout.receipt.order,
      isFetching: state.checkout.isFetching,
    }),
    {
      paymentResultRequest,
    }
  ),
)(Receipt);
